import type { CSSProperties } from 'react'

import { renderAs, type IntrinsicTag, type PolymorphicProps } from '@/components/motion/polymorphic'
import { revealMotion, type RevealVariant } from '@/lib/motion/reveal'
import { cn } from '@/lib/utils'

interface RevealOwnProps {
  variant: RevealVariant
  /** Its stagger step: 40ms each, capped at 320ms. */
  index?: number | undefined
  /** A delay of its own, as a CSS time, timed off something else. Wins over `index`. */
  delay?: string | undefined
  /** True once it has played, so a remount cannot replay it (`useMarksEntered`). */
  entered?: boolean | undefined
  className?: string | undefined
  style?: CSSProperties | undefined
}

export type RevealProps<T extends IntrinsicTag> = PolymorphicProps<T, RevealOwnProps>

/**
 * One element entering once: on mount, never on a re-render, a poll or a
 * search-param change, which keep it mounted. Renders the element itself (a
 * `div` unless `as` says otherwise), so it adds no box to the layout.
 *
 * Not for a list (`Stagger`), a bar (`BarFill`), a marker on a track
 * (`Marker`) or a floating surface (`OverlayMotion`). Under reduced motion the
 * global rule in globals.css collapses the entrance and its delay.
 */
export default function Reveal<T extends IntrinsicTag = 'div'>({
  as,
  variant,
  index,
  delay,
  entered,
  className,
  style,
  ...rest
}: RevealProps<T>) {
  const motion = revealMotion(variant, { index, delay, entered })
  return renderAs(as ?? 'div', {
    ...rest,
    className: cn(motion.className, className) || undefined,
    style: motion.style === undefined ? style : { ...motion.style, ...style },
  })
}
